"use client";

import type React from "react";
import type { ButtonProps } from "../types";

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  className = "",
  disabled = false,
  loading = false,
  fullWidth = false,
  onClick,
  type = "button",
}) => {
  // Variant styles based on prototype analysis
  const variantClasses = {
    primary: "bg-black text-white hover:bg-black/90 active:bg-black/80",
    secondary:
      "bg-white/40 text-black/80 hover:bg-white/60 border border-white/30 active:bg-white/70",
    ghost: "bg-transparent text-black/60 hover:text-black active:bg-black/20",
  };

  // Size variants
  const sizeClasses = {
    sm: "px-4 py-2 text-sm font-medium",
    md: "px-6 py-3 text-base font-medium",
    lg: "px-8 py-4 text-lg font-bold",
  };

  // Base classes
  const baseClasses =
    "rounded-md transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer active:scale-95 active:transform";

  // Width classes
  const widthClasses = fullWidth ? "w-full" : "";

  // Loading state
  const loadingClasses = loading ? "cursor-wait" : "";

  // Combine all classes
  const buttonClasses = [
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    widthClasses,
    loadingClasses,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      type={type}
      className={buttonClasses}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? (
        <div className="flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
          {children}
        </div>
      ) : (
        children
      )}
    </button>
  );
};

export default Button;
